import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { ArrowLeft, CaretLeft, CaretRight, House, CalendarBlank, ChartLineUp, Target, Medal } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/tyj7q2ct_logo%20365.png";
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const CalendarTracker = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [calendarData, setCalendarData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCalendarData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/calendar-data?year=${currentYear}`, { withCredentials: true, headers });
        setCalendarData(response.data.data || {});
      } catch (error) {
        console.error('Failed to fetch calendar data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCalendarData();
  }, [token, currentYear]);

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const renderMonthGrid = (monthIndex) => {
    const daysInMonth = getDaysInMonth(monthIndex, currentYear);
    const firstDay = getFirstDayOfMonth(monthIndex, currentYear);
    const today = new Date();
    const isCurrentMonth = today.getFullYear() === currentYear && today.getMonth() === monthIndex;
    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(<div key={`empty-${i}`} className="w-3 h-3" />);
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const hasWorkout = calendarData[dateStr];
      const isToday = isCurrentMonth && today.getDate() === day;
      const isPast = new Date(dateStr) < new Date(today.toDateString());
      days.push(<div key={day} className={`calendar-day ${hasWorkout ? 'completed' : isPast ? 'missed' : 'bg-card'} ${isToday ? 'ring-2 ring-[#D4AF37]' : ''}`} title={hasWorkout ? `${hasWorkout.total_minutes} min` : dateStr} />);
    }
    return days;
  };

  const completedDays = Object.keys(calendarData).length;

  return (
    <div className="min-h-screen bg-background pb-24 relative overflow-hidden" data-testid="calendar-screen">
      {/* Background logo */}
      <div 
        className="absolute inset-0 opacity-[0.03] bg-center bg-no-repeat bg-contain pointer-events-none"
        style={{ backgroundImage: `url(${LOGO_URL})` }}
      />

      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="w-10 h-10 rounded-full bg-card flex items-center justify-center" data-testid="back-btn"><ArrowLeft weight="bold" className="w-5 h-5 text-foreground" /></button>
          <div className="flex-1"><h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>365-Day Tracker</h1><p className="text-xs text-muted-foreground">{completedDays} days completed</p></div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-6 py-6 space-y-6 relative z-10">
        <div className="flex items-center justify-between">
          <button onClick={() => setCurrentYear(y => y - 1)} className="w-10 h-10 rounded-full bg-card flex items-center justify-center hover:bg-[#D4AF37]/10 transition-colors" data-testid="prev-year-btn"><CaretLeft weight="bold" className="w-5 h-5 text-foreground" /></button>
          <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{currentYear}</h2>
          <button onClick={() => setCurrentYear(y => y + 1)} className="w-10 h-10 rounded-full bg-card flex items-center justify-center hover:bg-[#D4AF37]/10 transition-colors" data-testid="next-year-btn"><CaretRight weight="bold" className="w-5 h-5 text-foreground" /></button>
        </div>

        <div className="flex items-center justify-center gap-6 text-xs">
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-[#D4AF37]" /><span className="text-muted-foreground">Completed</span></div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-sm bg-zinc-700" /><span className="text-muted-foreground">Missed</span></div>
        </div>

        {loading ? (<div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>) : (
          <div className="grid grid-cols-3 gap-4">{MONTHS.map((monthName, monthIndex) => (<div key={monthName} className="premium-card p-3"><p className="text-xs font-semibold text-[#D4AF37] mb-2 uppercase tracking-wider">{monthName.slice(0, 3)}</p><div className="grid grid-cols-7 gap-[2px]">{renderMonthGrid(monthIndex)}</div></div>))}</div>
        )}

        <div className="premium-card p-6">
          <h3 className="text-xs text-[#D4AF37] uppercase tracking-wider font-semibold mb-4">Year Summary</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div><p className="text-3xl font-black text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{completedDays}</p><p className="text-xs text-muted-foreground">Days Active</p></div>
            <div><p className="text-3xl font-black text-[#D4AF37]" style={{ fontFamily: 'Outfit, sans-serif' }}>{Math.round((completedDays / 365) * 100)}%</p><p className="text-xs text-muted-foreground">Progress</p></div>
            <div><p className="text-3xl font-black text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>{365 - completedDays}</p><p className="text-xs text-muted-foreground">Days Left</p></div>
          </div>
        </div>
      </main>

      <nav className="fixed bottom-0 left-0 right-0 bg-background/90 backdrop-blur-xl border-t border-border px-6 py-3 z-50">
        <div className="max-w-lg mx-auto flex items-center justify-around">
          <Link to="/dashboard" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-home"><House weight="duotone" className="w-6 h-6" /><span className="text-xs">Home</span></Link>
          <Link to="/calendar" className="flex flex-col items-center gap-1 text-[#D4AF37]" data-testid="nav-calendar"><CalendarBlank weight="fill" className="w-6 h-6" /><span className="text-xs">Calendar</span></Link>
          <Link to="/progress" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-progress"><ChartLineUp weight="duotone" className="w-6 h-6" /><span className="text-xs">Progress</span></Link>
          <Link to="/goals" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-goals"><Target weight="duotone" className="w-6 h-6" /><span className="text-xs">Goals</span></Link>
          <Link to="/milestones" className="flex flex-col items-center gap-1 text-muted-foreground hover:text-foreground transition-colors" data-testid="nav-milestones"><Medal weight="duotone" className="w-6 h-6" /><span className="text-xs">Badges</span></Link>
        </div>
      </nav>
    </div>
  );
};

export default CalendarTracker;
